import React, { useMemo, useState, useEffect } from "react"

import { Container, CardBody, Card } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import { axiosbaseurl } from "configure"
import moment from "moment"
import * as XLSX from "xlsx"

import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import CommonTable from "components/Common/CommonTable"
import Loader from "components/Common/Loader"

const ServiceFeeReport = () => {
  const [table_data, settable_data] = useState([])
  const [loading, setloading] = useState(false)
  const [selectedService, setSelectedService] = useState("")

  const getAllServiceFeeRecords = async () => {
    setloading(true)
    try {
      const response = await axiosbaseurl.get(`/reports/service/fee/report`)
      if (response.data.success) {
        settable_data(response.data.data)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setloading(false)
    }
  }

  useEffect(() => {
    getAllServiceFeeRecords()
  }, [])

  const serviceOptions = useMemo(
    () =>
      [...new Set(table_data.map(item => item.serviceName).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [table_data]
  )

  const filtered_data = useMemo(
    () =>
      selectedService
        ? table_data.filter(item => item.serviceName === selectedService)
        : table_data,
    [table_data, selectedService]
  )

  const exportToExcel = () => {
    if (!filtered_data.length) return

    const exportData = filtered_data.map((item, index) => ({
      "#": index + 1,
      "Client Code": item.clientCode,
      "Client Name": item.clientName,
      Service: item.serviceName,
      Fee: item.serviceFee,
      "Last Updated By": item.updatedBy,
      "Last Updated On": item.updatedAt
        ? moment(item.updatedAt).format("DD/MM/YYYY | hh:mm A")
        : "N/A",
      "Fee Prior To Update": item.priorFee,
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Service Fee Report")

    const maxWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(
        key.length,
        ...exportData.map(item => String(item[key]).length)
      ),
    }))
    worksheet["!cols"] = maxWidths

    XLSX.writeFileXLSX(
      workbook,
      `Service_Fee_Report_${moment().format("DD_MM_YYYY_hh_mm_ss")}.xlsx`
    )
  }

  document.title = "Service Fee Report | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}
      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb
            title={"Dashboard"}
            breadcrumbItem={"Service Fee Report"}
          />
          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                table_data={filtered_data}
                exportToExcel={exportToExcel}
                serviceOptions={serviceOptions}
                selectedService={selectedService}
                setSelectedService={setSelectedService}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({
  table_data,
  exportToExcel,
  serviceOptions,
  selectedService,
  setSelectedService,
}) => {
  const { checkAccess } = usePermissionData()
  const module_id = 24

  const navigate = useNavigate()

  // columns
  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        filterable: true,
        disableFilters: true,
        className: "text-center",
        Cell: cellProps => <span>{cellProps.row.index + 1}</span>,
      },
      {
        Header: "Client Code",
        accessor: "clientCode",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Client Name",
        accessor: "clientName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Service",
        accessor: "serviceName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Fee",
        accessor: "serviceFee",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Last Updated By",
        accessor: "updatedBy",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Last Updated On",
        accessor: "updatedAt",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => {
          return (
            <span>
              {cellProps.row.original?.updatedAt
                ? moment(cellProps.row.original?.updatedAt).format(
                    "DD/MM/YYYY | hh:mm A"
                  )
                : "N/A"}
            </span>
          )
        },
      },
      {
        Header: "Fee Prior To Update",
        accessor: "priorFee",
        disableFilters: true,
        filterable: true,
      },
    ],
    []
  )

  // table data
  const data = useMemo(() => table_data, [table_data])

  return (
    <>
      <PermittedComponent isAccess={checkAccess(module_id, "view")}>
        <CommonTable
          columns={columns}
          data={data}
          heading={
            <>
              <h5 className="fw-bold text-primary">Service Fee Report</h5>
              <h6 className="fw-medium">
                View fees charged across all clients
              </h6>
            </>
          }
        >
          <div className="d-flex">
            <div>
              <select
                className="form-select ms-2"
                value={selectedService}
                onChange={e => setSelectedService(e.target.value)}
              >
                <option value="">Filter By Service</option>
                {serviceOptions.map(service => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button
                onClick={exportToExcel}
                className="btn btn-primary bg-primary ms-3"
              >
                <i className="fas fa-download me-3"></i>
                Export
              </button>
            </div>
            <div>
              <button
                onClick={() => navigate(-1)}
                className="btn btn-secondary bg-dark ms-3"
              >
                <i className="fas fa-arrow-left me-3"></i>
                Back
              </button>
            </div>
          </div>
        </CommonTable>
      </PermittedComponent>
    </>
  )
}

export default ServiceFeeReport
